import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { increment } from "firebase/firestore";

import "./CrearCancionWeb.css";

// ✅ Solo cancionesWeb (agregué failed por si el webhook lo pone)
const STATUS_OPTIONS = ["pending", "ready", "created", "failed"];

function safeStr(v) {
  return (v ?? "").toString();
}

export async function notifySongUpdate(uid) {
  // ✅ opcional: si no hay uid, no truena
  if (!uid) return;
  const ref = doc(db, "usuarios", uid, "notifs", "summary");
  await setDoc(
    ref,
    {
      songUpdates: increment(1),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

function formatTs(ts) {
  try {
    if (!ts) return "—";
    const d = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
    return d.toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "—";
  }
}

export default function CrearCancionWeb() {
  const { songId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  // docs
  const [webDoc, setWebDoc] = useState(null); // ✅ cancionesWeb/{songId}
  const [songDoc, setSongDoc] = useState(null); // ⛔ ya no se usa, lo dejamos para "no quitar nada"

  // meta (✅ SOLO cancionesWeb)
  const usuarioId = webDoc?.usuarioId || "";

  // form
  const [titulo, setTitulo] = useState("");
  const [letra, setLetra] = useState("");
  const [estilo, setEstilo] = useState("");
  const [prompt, setPrompt] = useState("");

  const [coverUrl, setCoverUrl] = useState("");
  const [coverUrl02, setCoverUrl02] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [videoUrl02, setVideoUrl02] = useState("");

  const [tituloSubido, setTituloSubido] = useState("");
  const [status, setStatus] = useState("pending");
  const [pago, setPago] = useState(false);

  // solo mostrar (✅ SOLO cancionesWeb)
  const id = songId;
  const taskId = webDoc?.taskId || "";
  const fechaCreacion = webDoc?.createdAt || webDoc?.fechaCreacion;
  const fechaSubida = webDoc?.fechaSubida;
  const nombreCliente = webDoc?.nombreCliente || "";
  const correoGuest = webDoc?.emailGuest || "";
  const guestId = webDoc?.guestId || "";
  const shareLang = webDoc?.shareLang || "";

  const statusActualTexto = useMemo(() => {
    const s = safeStr(webDoc?.status);
    return s || "—";
  }, [webDoc]);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setMsg("");

      try {
        // ✅ SOLO leer cancionesWeb/{songId}
        const webRef = doc(db, "cancionesWeb", songId);
        const webSnap = await getDoc(webRef);

        if (!alive) return;

        const w = webSnap.exists() ? webSnap.data() : null;
        setWebDoc(w);

        // ✅ ya no leemos songDoc, pero lo ponemos null para que quede claro
        setSongDoc(null);

        hydrateFormFromDocs({ web: w });
        setLoading(false);
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setMsg("Error cargando datos.");
        setLoading(false);
      }
    }

    if (songId) load();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [songId]);

  // ✅ SOLO cancionesWeb
  function hydrateFormFromDocs({ web }) {
    const src = web || {};

    setTitulo(safeStr(src.titulo));
    setLetra(safeStr(src.letra));
    setEstilo(safeStr(src.estilo));
    setPrompt(safeStr(src.prompt));

    setCoverUrl(safeStr(src.coverUrl));
    setCoverUrl02(safeStr(src.coverUrl02));
    setVideoUrl(safeStr(src.videoUrl));
    setVideoUrl02(safeStr(src.videoUrl02));

    setTituloSubido(safeStr(src.tituloSubido));
    setStatus(STATUS_OPTIONS.includes(safeStr(src.status)) ? safeStr(src.status) : "pending");
    setPago(!!src.pago);
  }

  // ✅ NO cambiamos rutas (solo usa usuarioId para navegar, como lo tenías)
  const irASubirCancion = () => {
    if (!usuarioId || !songId) return;
    navigate(`/subircancion/${usuarioId}/${songId}`);
  };

  const irASubirDos = () => {
    if (!usuarioId || !songId) return;
    navigate(`/upload-two/${usuarioId}/${songId}`, { state: { usuarioId, songId } });
  };

  const handleGuardar = async () => {
    if (!songId) return;
    if (!usuarioId) {
      setMsg("No se encontró usuarioId en cancionesWeb. Agrega usuarioId para poder guardar.");
      return;
    }

    try {
      setSaving(true);
      setMsg("");

      // ✅ SOLO Guardar cancionesWeb/{songId}
      const webRef = doc(db, "cancionesWeb", songId);

      const cancionMini = {
        songId,
        usuarioId,

        // (si ya lo manejas)
        taskId: safeStr(taskId) || null,

        titulo: safeStr(titulo).trim() || null,
        letra: safeStr(letra),
        estilo: safeStr(estilo).trim(),
        prompt: safeStr(prompt),

        // urls para la página pública /s/:songId
        coverUrl: safeStr(coverUrl).trim(),
        coverUrl02: safeStr(coverUrl02).trim(),
        videoUrl: safeStr(videoUrl).trim(),
        videoUrl02: safeStr(videoUrl02).trim(),

        tituloSubido: safeStr(tituloSubido).trim(),

        // para filtros / inbox
        status: STATUS_OPTIONS.includes(status) ? status : "pending",
        pago: !!pago,

        // opcionales
        nombreCliente: safeStr(nombreCliente).trim() || null,

        updatedAt: serverTimestamp(),
      };

      await setDoc(webRef, cancionMini, { merge: true });

      notifySongUpdate(usuarioId);

      setMsg("Guardado correctamente ✅");

      // refrescar local (sin recargar)
      setWebDoc((prev) => ({ ...(prev || {}), ...cancionMini }));
      // ✅ songDoc ya no se usa
      setSongDoc(null);
    } catch (e) {
      console.error(e);
      setMsg("Error al guardar: " + (e?.message || "desconocido"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="container">
          <div className="card">
            <div className="profile-loading" style={{ marginTop: 0 }}>
              <div className="spinner" />
              <p>Cargando…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container">
        <div className="ccw-top">
          <div>
            <h1 className="h1">Crear canción (Web)</h1>
            <p className="p-muted" style={{ marginTop: 6 }}>
              {/* ✅ Mensaje actualizado para evitar confusión */}
              Edita y guarda SOLO en <strong>cancionesWeb/{songId}</strong>.
            </p>
          </div>

          <div className="ccw-actions">
            <button className="btn" onClick={() => navigate(-1)}>
              Volver
            </button>
            <button className="btn btn-primary" onClick={handleGuardar} disabled={saving}>
              {saving ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </div>

        {msg ? <div className="ccw-msg">{msg}</div> : null}

        {/* ====== SOLO MOSTRAR ====== */}
        <div className="card ccw-card">
          <div className="ccw-sectionTitle">Datos</div>

          <div className="ccw-grid">
            <div className="ccw-field">
              <div className="ccw-label">id</div>
              <div className="ccw-value mono">{id || "—"}</div>
            </div>

            <div className="ccw-field">
              <div className="ccw-label">usuarioId</div>
              <div className="ccw-value mono">{usuarioId || "—"}</div>
            </div>

            <div className="ccw-field">
              <div className="ccw-label">status actual</div>
              <div className="ccw-value">{statusActualTexto}</div>
            </div>

            <div className="ccw-field">
              <div className="ccw-label">Correo Guest</div>
              <div className="ccw-value mono">{correoGuest || "—"}</div>
            </div>

            <div className="ccw-field">
              <div className="ccw-label">GuestID</div>
              <div className="ccw-value mono">{guestId || "—"}</div>
            </div>

            <div className="ccw-field">
              <div className="ccw-label">Lenguaje compartido</div>
              <div className="ccw-value mono">{shareLang || "—"}</div>
            </div>

            <div className="ccw-field">
              <div className="ccw-label">taskId</div>
              <div className="ccw-value mono">{taskId || "—"}</div>
            </div>

            <div className="ccw-field">
              <div className="ccw-label">fechaCreacion</div>
              <div className="ccw-value">{formatTs(fechaCreacion)}</div>
            </div>

            <div className="ccw-field">
              <div className="ccw-label">fechaSubida</div>
              <div className="ccw-value">{formatTs(fechaSubida)}</div>
            </div>

            <div className="ccw-field">
              <div className="ccw-label">nombreCliente</div>
              <div className="ccw-value">{nombreCliente || "—"}</div>
            </div>
          </div>
        </div>

        {/* ====== EDITAR / CREAR ====== */}
        <div className="card ccw-card">
          <div className="ccw-sectionTitle">Editar</div>

          <div className="ccw-form">
            <div className="ccw-row">
              <label className="ccw-label2">Título</label>
              <input className="ccw-input" value={titulo} onChange={(e) => setTitulo(e.target.value)} />
            </div>

            <div className="ccw-row">
              <label className="ccw-label2">Estilo</label>
              <input className="ccw-input" value={estilo} onChange={(e) => setEstilo(e.target.value)} />
            </div>

            <div className="ccw-row">
              <label className="ccw-label2">Prompt</label>
              <textarea
                className="ccw-textarea"
                rows={3}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />
            </div>

            <div className="ccw-row">
              <label className="ccw-label2">Letra</label>
              <textarea
                className="ccw-textarea"
                rows={10}
                value={letra}
                onChange={(e) => setLetra(e.target.value)}
              />
            </div>

            <div className="ccw-split">
              <div className="ccw-row">
                <label className="ccw-label2">Status (editar)</label>
                <select className="ccw-input" value={status} onChange={(e) => setStatus(e.target.value)}>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ccw-row ccw-checkRow">
                <label className="ccw-label2">Pago</label>
                <label className="ccw-check">
                  <input type="checkbox" checked={pago} onChange={(e) => setPago(e.target.checked)} />
                  <span>Pagado</span>
                </label>
              </div>
            </div>

            <div className="ccw-row">
              <label className="ccw-label2">Título subido</label>
              <input
                className="ccw-input"
                value={tituloSubido}
                onChange={(e) => setTituloSubido(e.target.value)}
              />
            </div>

            {/* URLs */}
            <div className="ccw-row">
              <label className="ccw-label2">coverUrl</label>
              <input className="ccw-input" value={coverUrl} onChange={(e) => setCoverUrl(e.target.value)} />
              {coverUrl ? <audio controls style={{ width: "100%", marginTop: 6 }} src={coverUrl} /> : null}
            </div>

            <div className="ccw-row">
              <label className="ccw-label2">coverUrl02</label>
              <input className="ccw-input" value={coverUrl02} onChange={(e) => setCoverUrl02(e.target.value)} />
              {coverUrl02 ? <audio controls style={{ width: "100%", marginTop: 6 }} src={coverUrl02} /> : null}
            </div>

            <div className="ccw-uploadRow">
              <button className="btn" type="button" onClick={irASubirCancion} disabled={!usuarioId}>
                Subir MP3 (coverUrl)
              </button>
              <button className="btn" type="button" onClick={irASubirDos} disabled={!usuarioId}>
                Subir MP3 B (coverUrl02)
              </button>
            </div>

            <div className="ccw-row">
              <label className="ccw-label2">videoUrl</label>
              <input className="ccw-input" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} />
            </div>

            <div className="ccw-row">
              <label className="ccw-label2">videoUrl02</label>
              <input className="ccw-input" value={videoUrl02} onChange={(e) => setVideoUrl02(e.target.value)} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}